<template>
  <card v-if="user" :title="$t('pages.profile.sections.info.title')">
    <template #actions>
      <custom-button variant="primary" :busy="loading" @click.prevent="save">
        {{ $t('save') }}
      </custom-button>
    </template>
    <form ref="form" class="info-form" @submit.prevent="save">
      <TextInput
        v-model="rawUser.firstName"
        type="text"
        name="firstName"
        :error="errors?.firstName"
        :label="$t('user.firstName')"
      />
      <TextInput
        v-model="rawUser.lastName"
        type="text"
        name="lastName"
        :error="errors?.lastName"
        :label="$t('user.lastName')"
      />
      <TextInput
        :model-value="rawUser.email"
        type="email"
        name="email"
        :error="errors?.email"
        :label="$t('user.email') + '*'"
        required
        disabled
      />
      <TextInput
        v-model="rawUser.phoneNumber"
        type="text"
        name="phoneNumber"
        :error="errors?.phoneNumber"
        :label="$t('user.phoneNumber')"
      />
      <Multiselect
        v-model="rawUser.language"
        :options="languages"
        type="language"
        name="language"
        :can-clear="false"
        :can-deselect="false"
        :searchable="true"
        :label="$t('user.language')"
      />

      <Image ref="avatarRef" v-model="file" class="avatar-upload" use-height @click="open">
        <custom-button variant="outlined" class="px-5">
          {{ $t('uploadFile') }}
        </custom-button>
      </Image>
      <input type="submit" class="hidden" />
    </form>
  </card>
</template>

<script lang="ts" setup>
import Card from '@components/utils/Card.vue';
import TextInput from '@components/utils/inputs/Input.vue';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Multiselect from '@components/utils/Multiselect.vue';
import Image from '@/components/utils/inputs/Image.vue';
import { useStore } from '@/store';
import { enumOptionsProvider } from '@/components/utils/table/hooks/provider';
import { useFetchAction } from '@/hooks/fetch';
import type { User, WbFile } from '@/entities/Entities';
import { useNotify } from '@/components/utils/notifications';
import { syncRef } from '@/hooks/state';
import { Upload, useSingleFileUpload } from '@/components/utils/upload/fileUpload';
import { changeLocale } from '@/i18n';
import { useValidation } from '@/hooks/form';
import { useRouter } from 'vue-router';
import { object, string } from 'yup';

const store = useStore();
const form = ref<HTMLFormElement | null>(null);
const rawUser = syncRef(
  computed(() => store.state.auth.user),
  (u) => ({ ...u })
);
const { t, locale } = useI18n();
const router = useRouter();

const languages = enumOptionsProvider('/enums/Language', (value) => ({
  value,
  label: t(`language.${value}`),
}));

type ImageExposed = { upload: () => Promise<WbFile> };
const avatarRef = ref<ImageExposed | null>(null);
const [exec, { loading }] = useFetchAction();

const notify = useNotify();

const { file, open, uploadDiff, reset, getUpdatedFiles } = useSingleFileUpload<WbFile>(
  computed(() =>
    store.state.auth.user?.avatar ? Upload.from(store.state.auth.user.avatar) : undefined
  )
);

const schema = object({
  firstName: string().required(t('validation.notEmpty')),
  lastName: string().required(t('validation.notEmpty')),
  email: string().email().required(t('validation.notEmpty')),
  language: string(),
  phoneNumber: string()
    .matches(/^\+[1-9]\d{1,14}$/, {
      excludeEmptyString: true,
      message: t('user.validation.phoneNumber'),
    })
    .optional(),
});

const { data: user, errors, validatedFn } = useValidation(schema, rawUser);

const save = validatedFn(async () => {
  try {
    const uploadFiles = getUpdatedFiles(await uploadDiff());

    const body: Partial<User> = {
      firstName: user.value.firstName,
      lastName: user.value.lastName,
      email: user.value.email,
      language: user.value.language,
      phoneNumber: user.value.phoneNumber || null,
      avatar: (uploadFiles.at(0)?.file['@id'] ?? null) as unknown as WbFile,
    };

    await exec<User>(`/users/${user.value.id}`, {
      method: 'PUT',
      body,
    });
    reset();

    if (user.value.language && user.value.language !== locale.value) {
      changeLocale(router, user.value.language);
    }

    notify?.({
      variant: 'success',
      title: t('pages.profile.sections.info.saveSuccess'),
    });
    store.dispatch('auth/LOAD_USER');
  } catch (e) {
    console.error(e);
    notify?.({
      variant: 'error',
      title: t('pages.profile.sections.info.saveError'),
    });
  }
});
</script>

<style lang="scss" scoped>
.info-form {
  @apply grid grid-cols-2 gap-y-5 gap-x-4;
}

:deep(.drop-box) {
  @apply grid items-end justify-between;
  @apply py-7 pl-4 pr-4.5;
  grid-template-columns: max-content min-content;
  height: px(175);
}

.avatar-upload {
  grid-column: 1;
}
</style>
